<template>
  <v-card>
    <v-toolbar
      class="settings-window-toolbar"
      flat
      height="80"
    >
      <v-avatar
        :color="$t('Settings.Order.Toolbar.Color')"
      >
        <v-icon dark>
          {{ $t('Settings.Order.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.Order.Toolbar.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.Order.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      style="overflow-y: auto;"
    >
      <template v-if="viewData">
        <v-form
          ref="settingsForm"
          v-model="settingsFormValid"
          autocomplete="off"
          @submit.prevent
        >
          <v-card flat>
            <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
              <v-icon class="pr-3">
                {{ $t('Settings.Order.Field.GeneralOption.Icon') }}
              </v-icon>

              <h5 class="text-body-2 font-weight-bold">
                <lang-search-highlight
                  :lang-text="$t('Settings.Order.Field.GeneralOption.Title')"
                  :search-text="langSearchText"
                  highlight
                />
              </h5>
            </v-card-title>

            <v-divider />

            <v-card-text>
              <v-container
                class="pa-0"
                fluid
              >
                <v-row
                  align="center"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTips.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTips.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.OrderTips"
                      :disabled="!userCanManage"
                      :label="viewData.OrderTips ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatus.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatus.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-select
                      v-model="viewData.DefaultOrderStatus"
                      :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                      :label="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                      class="pt-2"
                      clearable
                      filled
                      hide-details
                      item-text="Name"
                      item-value="Id"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-select
                      v-model="viewData.DefaultOrderStatusPos"
                      :items="$te('Settings.Payment.Edit.Field.OrderStatusPos.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatusPos.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatusPos.Items') : []"
                      :label="$t('Settings.Payment.Edit.Field.OrderStatusPos.Label')"
                      class="pt-2"
                      clearable
                      filled
                      hide-details
                      item-text="Name"
                      item-value="Id"
                    />
                  </v-col>

                  <v-col cols="12" />
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatusPerPaymentMethod.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatusPerPaymentMethod.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.DefaultOrderStatusOverride"
                      :disabled="!userCanManage"
                      :false-value="true"
                      :label="viewData.DefaultOrderStatusOverride ? $t('Common.Button.Toggle.Inactive') : $t('Common.Button.Toggle.Active')"
                      :true-value="false"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <template v-if="!viewData.DefaultOrderStatusOverride && !!activePaymentMethods.length">
                    <v-col cols="12">
                      <v-card
                        :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        flat
                      >
                        <v-card-text class="pa-2">
                          <v-simple-table>
                            <template #default>
                              <thead>
                                <tr>
                                  <th
                                    class="text-left pa-1"
                                    width="340"
                                  >
                                    {{ $t('Order.SourceType.Title') }}
                                  </th>

                                  <th
                                    class="text-left pa-1"
                                    width="440"
                                  >
                                    {{ $t('Settings.Payment.Edit.Field.OrderStatus.Label') }}
                                  </th>

                                  <th
                                    class="text-left pa-1"
                                    width="440"
                                  >
                                    {{ $t('Settings.Payment.Edit.Field.OrderStatusPos.Label') }}
                                  </th>

                                  <th class="text-left pa-1" />
                                </tr>
                              </thead>

                              <tbody>
                                <template v-for="(paymentMethod, index) in activePaymentMethods">
                                  <tr :key="`status-overide-payment-method-${paymentMethod.Code}-${index}`">
                                    <td class="pa-1 text-left">
                                      <v-chip
                                        :key="paymentMethod.Code"
                                        :color="paymentMethod.Color1"
                                        class="white--text elevation-0 mr-1 pl-0 text-caption"
                                        label
                                        style="text-transform: uppercase; min-width: 300px;"
                                      >
                                        <v-avatar
                                          :color="paymentMethod.Color2"
                                          class="mr-2"
                                          style="width: 46px !important; height: 32px !important;"
                                          tile
                                        >
                                          <v-icon
                                            class="white--text"
                                            color="white"
                                          >
                                            {{ paymentMethod.Code === PaymentTypeEnum.CASH ? appCurrency.Icon : paymentMethod.Icon }}
                                          </v-icon>
                                        </v-avatar>

                                        <div
                                          class="text-center "
                                          style="width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                        >
                                          {{
                                            paymentMethod.Code === PaymentTypeEnum.CASH ? $t('Order.PaymentMethod.Cash').Title.toLocaleUpperCase($i18n.locale) : paymentMethod.Code === PaymentTypeEnum.CARD ? $t('Order.PaymentMethod.Card').Title.toLocaleUpperCase($i18n.locale) : paymentMethod.Title.toLocaleUpperCase($i18n.locale)
                                          }}
                                        </div>
                                      </v-chip>
                                    </td>

                                    <td class="pa-1 text-left">
                                      <v-select
                                        v-if="paymentMethod.Site"
                                        v-model="paymentMethod.OrderStatus"
                                        :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                                        :placeholder="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                                        dense
                                        filled
                                        hide-details
                                        item-text="Name"
                                        item-value="Id"
                                        style="max-width: 400px;"
                                      />
                                    </td>

                                    <td class="pa-1 text-left">
                                      <v-select
                                        v-if="paymentMethod.Pos"
                                        v-model="paymentMethod.OrderStatusPos"
                                        :items="$te('Settings.Payment.Edit.Field.OrderStatusPos.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatusPos.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatusPos.Items') : []"
                                        :placeholder="$t('Settings.Payment.Edit.Field.OrderStatusPos.Label')"
                                        dense
                                        filled
                                        hide-details
                                        item-text="Name"
                                        item-value="Id"
                                        style="max-width: 400px;"
                                      />
                                    </td>

                                    <td class="pa-1 text-left" />
                                  </tr>
                                </template>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </template>

                  <v-col
                    class="my-2"
                    cols="12"
                  />
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatusPerSourceType.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatusPerSourceType.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.DefaultOrderStatusOverrideBySourceType.Enabled"
                      :disabled="!userCanManage"
                      :label="viewData.DefaultOrderStatusOverrideBySourceType.Enabled ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <template v-if="viewData.DefaultOrderStatusOverrideBySourceType.Enabled">
                    <v-col cols="12">
                      <v-card
                        :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        flat
                      >
                        <v-card-text class="pa-2">
                          <v-simple-table>
                            <template #default>
                              <thead>
                                <tr>
                                  <th
                                    class="text-left pa-1"
                                    width="250"
                                  >
                                    {{ $t('Order.SourceType.Title') }}
                                  </th>

                                  <th
                                    class="text-left pa-1"
                                  >
                                    {{ $t('Settings.Payment.Edit.Field.OrderStatusPos.Label') }}
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                <template v-for="(sourceType, sourceTypeIdx) in viewData.DefaultOrderStatusOverrideBySourceType.SourceTypes">
                                  <tr :key="`order-source-type${sourceType.SourceTypeId}-default-status-${sourceTypeIdx}`">
                                    <td class="pa-1 text-left">
                                      <order-source-type-chip :source-type="getActiveOrderSourceTypeById(sourceType.SourceTypeId)" />
                                    </td>

                                    <td class="pa-1 text-left">
                                      <v-select
                                        v-model="sourceType.DefaultOrderStatusPos"
                                        :items="$te('Settings.Payment.Edit.Field.OrderStatusPos.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatusPos.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatusPos.Items') : []"
                                        :placeholder="$t('Settings.Payment.Edit.Field.OrderStatusPos.Label')"
                                        dense
                                        filled
                                        hide-details
                                        item-text="Name"
                                        item-value="Id"
                                        style="max-width: 400px;"
                                      />
                                    </td>
                                  </tr>
                                </template>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </template>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                  </v-col>
                  <v-col>
                    <div class="text-caption py-2">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.ShowOrderType')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </div>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.ShowOrderTypes"
                      :disabled="!userCanManage"
                      :label="viewData.ShowOrderTypes ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="blue"
                      hide-details
                      inset
                    />
                  </v-col>
                  <v-col
                    v-if="viewData.ShowOrderTypes"
                    cols="12"
                  >
                    <h5 class="mt-2">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.Label.DefaultTypeWebsite')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <v-btn-toggle
                      v-model="viewData.DefaultOrderType"
                      class="lighten-2 elevation-0"
                      mandatory
                      style="width: 100%;"
                    >
                      <v-btn
                        :style="viewData.DefaultOrderType === 1 ? 'opacity: 1' : 'opacity: 0.35'"
                        :value="1"
                        class="purple white--text elevation-0 mr-0"
                        large
                        style="width: 50%; font-size: 13px !important;"
                      >
                        <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Title') }}</span>
                        <v-icon
                          class="white--text"
                          right
                        >
                          {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Icon') }}
                        </v-icon>
                      </v-btn>

                      <v-btn
                        :style="viewData.DefaultOrderType === 2 ? 'opacity: 1' : 'opacity: 0.35'"
                        :value="2"
                        class="blue darken-3 white--text elevation-0 ml-0"
                        large
                        style="width: 50%; font-size: 13px !important;"
                      >
                        <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Title') }}</span>
                        <v-icon
                          class="white--text"
                          right
                        >
                          {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Icon') }}
                        </v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>

                  <v-col cols="12">
                    <h5 class="mt-2">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.Label.DefaultTypePos')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <v-btn-toggle
                      v-model="viewData.DefaultOrderTypePos"
                      class="lighten-2 elevation-0"
                      mandatory
                      style="width: 100%;"
                    >
                      <v-btn
                        :style="viewData.DefaultOrderTypePos === 1 ? 'opacity: 1' : 'opacity: 0.35'"
                        :value="1"
                        class="purple white--text elevation-0 mr-0"
                        large
                        style="width: 50%; font-size: 13px !important;"
                      >
                        <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Title') }}</span>
                        <v-icon
                          class="white--text"
                          right
                        >
                          {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Icon') }}
                        </v-icon>
                      </v-btn>

                      <v-btn
                        :style="viewData.DefaultOrderTypePos === 2 ? 'opacity: 1' : 'opacity: 0.35'"
                        :value="2"
                        class="blue darken-3 white--text elevation-0 ml-0"
                        large
                        style="width: 50%; font-size: 13px !important;"
                      >
                        <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Title') }}</span>
                        <v-icon
                          class="white--text"
                          right
                        >
                          {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Icon') }}
                        </v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>

                  <v-col cols="12">
                    <h5 class="mt-2">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.Label.DefaultTypePhone')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <v-btn-toggle
                      v-model="viewData.DefaultOrderTypePhone"
                      class="lighten-2 elevation-0"
                      mandatory
                      style="width: 100%;"
                    >
                      <v-btn
                        :style="viewData.DefaultOrderTypePhone === 1 ? 'opacity: 1' : 'opacity: 0.35'"
                        :value="1"
                        class="purple white--text elevation-0 mr-0"
                        large
                        style="width: 50%; font-size: 13px !important;"
                      >
                        <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Title') }}</span>
                        <v-icon
                          class="white--text"
                          right
                        >
                          {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Icon') }}
                        </v-icon>
                      </v-btn>

                      <v-btn
                        :style="viewData.DefaultOrderTypePhone === 2 ? 'opacity: 1' : 'opacity: 0.35'"
                        :value="2"
                        class="blue darken-3 white--text elevation-0 ml-0"
                        large
                        style="width: 50%; font-size: 13px !important;"
                      >
                        <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Title') }}</span>
                        <v-icon
                          class="white--text"
                          right
                        >
                          {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Icon') }}
                        </v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>

                  <v-col cols="12">
                    <h5 class="mt-2">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.OrderTotalMin.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row dense>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="viewData.OrderTotalDeliveryMin"
                            :label="$t('Settings.Order.Field.GeneralOption.OrderTypes.OrderTotalMin.OrderTotalDeliveryMin')"
                            clearable
                            filled
                            hide-details
                            min="0"
                            step="any"
                            type="number"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="viewData.OrderTotalCollectionMin"
                            :label="$t('Settings.Order.Field.GeneralOption.OrderTypes.OrderTotalMin.OrderTotalCollectionMin')"
                            clearable
                            filled
                            hide-details
                            min="0"
                            step="any"
                            type="number"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>

                  <v-col
                    v-if="!!appConfig?.LOCATION_DATA?.BelongsToMarketplace"
                    cols="12"
                  >
                    <h5 class="mt-2">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTypes.OrderTotalMin.MarketPlaceTitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row dense>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="viewData.MarketplaceOrderTotalDeliveryMin"
                            :label="$t('Settings.Order.Field.GeneralOption.OrderTypes.OrderTotalMin.OrderTotalDeliveryMin')"
                            clearable
                            filled
                            hide-details
                            min="0"
                            step="any"
                            type="number"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="viewData.MarketplaceOrderTotalCollectionMin"
                            :label="$t('Settings.Order.Field.GeneralOption.OrderTypes.OrderTotalMin.OrderTotalCollectionMin')"
                            clearable
                            filled
                            hide-details
                            min="0"
                            step="any"
                            type="number"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.DailyOrders.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.DailyOrders.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.OrdersDailyOverride"
                      :disabled="!userCanManage"
                      :label="viewData.OrdersDailyOverride ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12" />

                  <v-col cols="6">
                    <v-menu
                      v-model="menuOrdersDailyOpenVisible"
                      :close-on-content-click="false"
                      :disabled="!viewData.OrdersDailyOverride"
                      offset-y
                      transition="scale-transition"
                    >
                      <template #activator="{on}">
                        <v-text-field
                          v-model="viewData.OrdersDailyOpen"
                          :disabled="!viewData.OrdersDailyOverride"
                          :label="$t('Settings.Order.Field.GeneralOption.DailyOrders.From')"
                          :prepend-icon="$t('Settings.Order.Field.GeneralOption.DailyOrders.Icon')"
                          readonly
                          required
                          v-on="on"
                        />
                      </template>

                      <v-time-picker
                        v-model="viewData.OrdersDailyOpen"
                        :disabled="!viewData.OrdersDailyOverride"
                        :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                        first-day-of-week="1"
                        format="24hr"
                        scrollable
                        @input="menuOrdersDailyOpenVisible = false"
                      />
                    </v-menu>
                  </v-col>

                  <v-col cols="6">
                    <v-menu
                      v-model="menuOrdersDailyCloseVisible"
                      :close-on-content-click="false"
                      :disabled="!viewData.OrdersDailyOverride"
                      offset-y
                      transition="scale-transition"
                    >
                      <template #activator="{on}">
                        <v-text-field
                          v-model="viewData.OrdersDailyClose"
                          :disabled="!viewData.OrdersDailyOverride"
                          :label="$t('Settings.Order.Field.GeneralOption.DailyOrders.To')"
                          :prepend-icon="$t('Settings.Order.Field.GeneralOption.DailyOrders.Icon')"
                          readonly
                          required
                          v-on="on"
                        />
                      </template>

                      <v-time-picker
                        v-model="viewData.OrdersDailyClose"
                        :disabled="!viewData.OrdersDailyOverride"
                        :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                        first-day-of-week="1"
                        format="24hr"
                        scrollable
                        @input="menuOrdersDailyCloseVisible = false"
                      />
                    </v-menu>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Order Time Update -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTimeUpdate.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderTimeUpdate.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-card
                      :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                      flat
                    >
                      <v-card-text class="pa-2">
                        <v-simple-table>
                          <template #default>
                            <thead>
                              <tr>
                                <th class="text-center pa-1">
                                  {{ $t('Order.SourceType.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Order.FilterStatuses.Title') }}
                                </th>

                                <th class="text-center pa-1">
                                  {{ $t('Common.Button.Toggle.ActiveF') }}
                                </th>

                                <th class="text-right pa-1">
                                  <v-btn
                                    class="primary white--text"
                                    depressed
                                    icon
                                    x-small
                                    @click="addOrderTimeUpdateSource"
                                  >
                                    <v-icon>mdi-plus</v-icon>
                                  </v-btn>
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              <template v-for="(sourceType, sourceTypeIdx) in viewData.OrderTimeUpdate">
                                <tr :key="`source-type-${sourceTypeIdx}`">
                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.source"
                                      :disabled="sourceType.disabled || !userCanManage"
                                      :items="activeSourceTypesGroupsWithGroups"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon
                                              v-if="item.Avatar.Icon"
                                              class="white--text"
                                            >
                                              {{ item.Avatar.Icon }}
                                            </v-icon>

                                            <img
                                              v-else
                                              :src="item.Avatar.Img"
                                            >
                                          </v-avatar>
                                          <span>{{ item.Description }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar
                                          :color="data.item.Avatar.Color"
                                          :size="28"
                                        >
                                          <v-icon
                                            v-if="data.item.Avatar.Icon"
                                            class="white--text"
                                          >
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>

                                          <img
                                            v-else
                                            :src="data.item.Avatar.Img"
                                          >
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                          <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-select
                                      v-model="sourceType.status"
                                      :disabled="sourceType.disabled || !userCanManage"
                                      :items="orderStatusesFiltered"
                                      :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                      class="d-inline-flex"
                                      dense
                                      hide-details
                                      item-text="Name"
                                      item-value="Id"
                                      outlined
                                      single-line
                                      style="max-width: 300px;"
                                    >
                                      <template #selection="{item}">
                                        <v-chip
                                          :color="item.Status ? 'green' : ''"
                                          :dark="item.Status"
                                          class="pl-0"
                                          label
                                          small
                                          style="width: 100%;"
                                        >
                                          <v-avatar
                                            :color="item.Avatar.Color"
                                            class="mr-2"
                                            tile
                                          >
                                            <v-icon class="white--text">
                                              {{ item.Avatar.Icon }}
                                            </v-icon>
                                          </v-avatar>
                                          <span>{{ item.Name }}</span>
                                        </v-chip>
                                      </template>

                                      <template #item="data">
                                        <v-list-item-avatar :color="data.item.Avatar.Color">
                                          <v-icon dark>
                                            {{ data.item.Avatar.Icon }}
                                          </v-icon>
                                        </v-list-item-avatar>

                                        <v-list-item-content>
                                          <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                        </v-list-item-content>
                                      </template>
                                    </v-select>
                                  </td>

                                  <td class="pa-1 text-center">
                                    <v-switch
                                      v-model="sourceType.active"
                                      :disabled="sourceType.disabled || !userCanManage"
                                      class="ma-0 pa-0 d-inline-flex"
                                      color="success"
                                      hide-details
                                      inset
                                    />
                                  </td>

                                  <td class="pa-1 text-right">
                                    <v-btn
                                      v-if="!sourceType.disabled"
                                      :disabled="sourceType.disabled || !userCanManage"
                                      class="red white--text"
                                      depressed
                                      icon
                                      x-small
                                      @click="removeOrderTimeUpdateSource(sourceTypeIdx)"
                                    >
                                      <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </template>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Order Statuses -->
                <v-row
                  v-if="userIsSuperAdmin"
                  dense
                >
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatusFlow.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderStatusFlow.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-btn
                      class="primary white--text mr-2 mt-1"
                      depressed
                      icon
                      small
                      @click="addOrderStatusSource"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>

                  <v-col cols="12">
                    <template v-for="(orderStatus, orderStatusIdx) in viewData.OrderStatuses">
                      <v-card
                        v-if="activeSourceTypesAll.find(source => source.Id === parseInt(orderStatus.Source) || !orderStatus.Source)"
                        :key="`orders-status-${orderStatusIdx}`"
                        :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        class="mb-6"
                        flat
                      >
                        <v-card-text class="pa-2">
                          <v-container
                            fluid
                            pa-0
                          >
                            <v-row dense>
                              <v-col>
                                <v-select
                                  v-model="orderStatus.Source"
                                  :disabled="!userCanManage"
                                  :items="orderStatusFromToSourceTypes"
                                  :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                                  full-width
                                  hide-details
                                  item-text="Name"
                                  item-value="Id"
                                  outlined
                                  single-line
                                >
                                  <template #selection="{item}">
                                    <v-chip
                                      :color="item.Status ? 'green' : ''"
                                      :dark="item.Status"
                                      class="pa-0"
                                      label
                                      style="width: 100%;"
                                    >
                                      <v-avatar
                                        :color="item.Avatar.Color"
                                        class="mr-2"
                                        style="width: 32px !important; height: 32px !important;"
                                        tile
                                      >
                                        <v-icon
                                          v-if="item.Avatar.Icon"
                                          class="white--text"
                                        >
                                          {{ item.Avatar.Icon }}
                                        </v-icon>

                                        <img
                                          v-else
                                          :src="item.Avatar.Img"
                                          style="width: 32px !important; height: 32px !important;"
                                        >
                                      </v-avatar>
                                      <span>{{ item.Description }}</span>
                                    </v-chip>
                                  </template>

                                  <template #item="data">
                                    <v-list-item-avatar
                                      :color="data.item.Avatar.Color"
                                      :size="28"
                                    >
                                      <v-icon
                                        v-if="data.item.Avatar.Icon"
                                        class="white--text"
                                      >
                                        {{ data.item.Avatar.Icon }}
                                      </v-icon>

                                      <img
                                        v-else
                                        :src="data.item.Avatar.Img"
                                      >
                                    </v-list-item-avatar>

                                    <v-list-item-content>
                                      <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                      <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </template>
                                </v-select>
                              </v-col>

                              <v-col class="shrink align-center justify-center">
                                <v-btn
                                  :disabled="!userCanManage"
                                  class="red white--text mt-4"
                                  depressed
                                  icon
                                  small
                                  @click="removeOrderStatusSource(orderStatusIdx)"
                                >
                                  <v-icon>mdi-close</v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-container>

                          <v-row class="mt-2">
                            <v-col
                              cols="12"
                              md="6"
                            >
                              <v-simple-table
                                class="pa-2 rounded"
                                style="border: thin solid rgba(255, 255, 255, 0.20);"
                              >
                                <thead>
                                  <tr>
                                    <th
                                      :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-3'"
                                      class="text-center text-h6 pa-1"
                                      colspan="3"
                                    >
                                      Normal
                                    </th>
                                  </tr>
                                  <tr>
                                    <th class="text-center pa-1">
                                      {{ $t('Settings.Order.Field.GeneralOption.OrderStatusFlow.StatusFrom') }}
                                    </th>

                                    <th class="text-center pa-1">
                                      {{ $t('Settings.Order.Field.GeneralOption.OrderStatusFlow.StatusTo') }}
                                    </th>

                                    <th
                                      class="text-center pa-1"
                                      style="width: 70px;"
                                    >
                                      <v-btn
                                        class="primary white--text"
                                        depressed
                                        icon
                                        x-small
                                        @click="addOrderStatusSourceNormal(orderStatusIdx)"
                                      >
                                        <v-icon>mdi-plus</v-icon>
                                      </v-btn>

                                      <v-btn
                                        v-if="!!viewData.OrderStatusesDefaults.find(o => o.Source === orderStatus.Source)"
                                        class="warning white--text ml-2"
                                        depressed
                                        icon
                                        x-small
                                        @click="resetOrderStatusSourceNormal(orderStatusIdx, orderStatus.Source)"
                                      >
                                        <v-icon>mdi-refresh</v-icon>
                                      </v-btn>
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <template v-for="(orderStatusNormal, orderStatusNormalIdx) in orderStatus.Status.Normal">
                                    <tr :key="`orders-status-${orderStatusIdx}-normal-${orderStatusNormalIdx}`">
                                      <td class="pa-1 text-center">
                                        <v-select
                                          v-model="orderStatusNormal.From"
                                          :disabled="!userCanManage"
                                          :items="orderStatusesFromTo"
                                          :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                          class="d-inline-flex"
                                          dense
                                          hide-details
                                          item-text="Name"
                                          item-value="Id"
                                          outlined
                                          single-line
                                          style="min-width: 250px; max-width: 250px;"
                                        >
                                          <template #selection="{item}">
                                            <v-chip
                                              :color="item.Status ? 'green' : ''"
                                              :dark="item.Status"
                                              class="pl-0"
                                              label
                                              small
                                              style="width: 100%;"
                                            >
                                              <v-avatar
                                                :color="item.Avatar.Color"
                                                class="mr-2"
                                                tile
                                              >
                                                <v-icon class="white--text">
                                                  {{ item.Avatar.Icon }}
                                                </v-icon>
                                              </v-avatar>
                                              <span>{{ item.Name }}</span>
                                            </v-chip>
                                          </template>

                                          <template #item="data">
                                            <v-list-item-avatar :color="data.item.Avatar.Color">
                                              <v-icon dark>
                                                {{ data.item.Avatar.Icon }}
                                              </v-icon>
                                            </v-list-item-avatar>

                                            <v-list-item-content>
                                              <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                            </v-list-item-content>
                                          </template>
                                        </v-select>
                                      </td>

                                      <td class="pa-1 text-center">
                                        <v-select
                                          v-model="orderStatusNormal.To"
                                          :disabled="!userCanManage"
                                          :items="orderStatusesFromTo"
                                          :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                          class="d-inline-flex"
                                          dense
                                          hide-details
                                          item-text="Name"
                                          item-value="Id"
                                          outlined
                                          single-line
                                          style="min-width: 250px; max-width: 250px;"
                                        >
                                          <template #selection="{item}">
                                            <v-chip
                                              :color="item.Status ? 'green' : ''"
                                              :dark="item.Status"
                                              class="pl-0"
                                              label
                                              small
                                              style="width: 100%;"
                                            >
                                              <v-avatar
                                                :color="item.Avatar.Color"
                                                class="mr-2"
                                                tile
                                              >
                                                <v-icon class="white--text">
                                                  {{ item.Avatar.Icon }}
                                                </v-icon>
                                              </v-avatar>
                                              <span>{{ item.Name }}</span>
                                            </v-chip>
                                          </template>

                                          <template #item="data">
                                            <v-list-item-avatar :color="data.item.Avatar.Color">
                                              <v-icon dark>
                                                {{ data.item.Avatar.Icon }}
                                              </v-icon>
                                            </v-list-item-avatar>

                                            <v-list-item-content>
                                              <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                            </v-list-item-content>
                                          </template>
                                        </v-select>
                                      </td>

                                      <td class="pa-1 text-center">
                                        <v-btn
                                          :disabled="!userCanManage"
                                          class="red white--text"
                                          depressed
                                          icon
                                          x-small
                                          @click="removeOrderStatusSourceNormal(orderStatusIdx, orderStatusNormalIdx)"
                                        >
                                          <v-icon>mdi-close</v-icon>
                                        </v-btn>
                                      </td>
                                    </tr>
                                  </template>
                                </tbody>
                              </v-simple-table>
                            </v-col>

                            <v-col
                              cols="12"
                              md="6"
                            >
                              <v-simple-table
                                class="pa-2 rounded"
                                style="border: thin solid rgba(255, 255, 255, 0.20);"
                              >
                                <thead>
                                  <tr>
                                    <th
                                      :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-3'"
                                      class="text-center text-h6 pa-1"
                                      colspan="3"
                                    >
                                      Scheduled
                                    </th>
                                  </tr>
                                  <tr>
                                    <th class="text-center pa-1">
                                      {{ $t('Settings.Order.Field.GeneralOption.OrderStatusFlow.StatusFrom') }}
                                    </th>

                                    <th class="text-center pa-1">
                                      {{ $t('Settings.Order.Field.GeneralOption.OrderStatusFlow.StatusTo') }}
                                    </th>

                                    <th
                                      class="text-center pa-1"
                                      style="width: 70px;"
                                    >
                                      <v-btn
                                        class="primary white--text"
                                        depressed
                                        icon
                                        x-small
                                        @click="addOrderStatusSourceScheduled(orderStatusIdx)"
                                      >
                                        <v-icon>mdi-plus</v-icon>
                                      </v-btn>

                                      <v-btn
                                        v-if="!!viewData.OrderStatusesDefaults.find(o => o.Source === orderStatus.Source)"
                                        class="warning white--text ml-2"
                                        depressed
                                        icon
                                        x-small
                                        @click="resetOrderStatusSourceScheduled(orderStatusIdx, orderStatus.Source)"
                                      >
                                        <v-icon>mdi-refresh</v-icon>
                                      </v-btn>
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <template v-for="(orderStatusScheduled, orderStatusScheduledIdx) in orderStatus.Status.Scheduled">
                                    <tr :key="`orders-status-${orderStatusIdx}-scheduled-${orderStatusScheduledIdx}`">
                                      <td class="pa-1 text-center">
                                        <v-select
                                          v-model="orderStatusScheduled.From"
                                          :disabled="!userCanManage"
                                          :items="orderStatusesFromTo"
                                          :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                          class="d-inline-flex"
                                          dense
                                          hide-details
                                          item-text="Name"
                                          item-value="Id"
                                          outlined
                                          single-line
                                          style="min-width: 250px; max-width: 250px;"
                                        >
                                          <template #selection="{item}">
                                            <v-chip
                                              :color="item.Status ? 'green' : ''"
                                              :dark="item.Status"
                                              class="pl-0"
                                              label
                                              small
                                              style="width: 100%;"
                                            >
                                              <v-avatar
                                                :color="item.Avatar.Color"
                                                class="mr-2"
                                                tile
                                              >
                                                <v-icon class="white--text">
                                                  {{ item.Avatar.Icon }}
                                                </v-icon>
                                              </v-avatar>
                                              <span>{{ item.Name }}</span>
                                            </v-chip>
                                          </template>

                                          <template #item="data">
                                            <v-list-item-avatar :color="data.item.Avatar.Color">
                                              <v-icon dark>
                                                {{ data.item.Avatar.Icon }}
                                              </v-icon>
                                            </v-list-item-avatar>

                                            <v-list-item-content>
                                              <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                            </v-list-item-content>
                                          </template>
                                        </v-select>
                                      </td>

                                      <td class="pa-1 text-center">
                                        <v-select
                                          v-model="orderStatusScheduled.To"
                                          :disabled="!userCanManage"
                                          :items="orderStatusesFromTo"
                                          :label="$t('Restaurant.CatalogAddDialog.Field.OrderStatus.Label')"
                                          class="d-inline-flex"
                                          dense
                                          hide-details
                                          item-text="Name"
                                          item-value="Id"
                                          outlined
                                          single-line
                                          style="min-width: 250px; max-width: 250px;"
                                        >
                                          <template #selection="{item}">
                                            <v-chip
                                              :color="item.Status ? 'green' : ''"
                                              :dark="item.Status"
                                              class="pl-0"
                                              label
                                              small
                                              style="width: 100%;"
                                            >
                                              <v-avatar
                                                :color="item.Avatar.Color"
                                                class="mr-2"
                                                tile
                                              >
                                                <v-icon class="white--text">
                                                  {{ item.Avatar.Icon }}
                                                </v-icon>
                                              </v-avatar>
                                              <span>{{ item.Name }}</span>
                                            </v-chip>
                                          </template>

                                          <template #item="data">
                                            <v-list-item-avatar :color="data.item.Avatar.Color">
                                              <v-icon dark>
                                                {{ data.item.Avatar.Icon }}
                                              </v-icon>
                                            </v-list-item-avatar>

                                            <v-list-item-content>
                                              <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                            </v-list-item-content>
                                          </template>
                                        </v-select>
                                      </td>

                                      <td class="pa-1 text-center">
                                        <v-btn
                                          :disabled="!userCanManage"
                                          class="red white--text"
                                          depressed
                                          icon
                                          x-small
                                          @click="removeOrderStatusSourceScheduled(orderStatusIdx, orderStatusScheduledIdx)"
                                        >
                                          <v-icon>mdi-close</v-icon>
                                        </v-btn>
                                      </td>
                                    </tr>
                                  </template>
                                </tbody>
                              </v-simple-table>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </template>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>
                </v-row>

                <!-- Scheduled Order Settings -->
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderScheduled.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.GeneralOption.OrderScheduled.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="6">
                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row dense>
                        <v-col cols="12">
                          <v-sheet
                            class="purple white--text pa-2 mb-2"
                            rounded
                          >
                            <v-row class="align-center">
                              <v-col>
                                <div class="d-inline-flex">
                                  <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Title') }}</span>
                                  <v-icon
                                    class="white--text ml-2"
                                    small
                                  >
                                    {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Delivery.Icon') }}
                                  </v-icon>
                                </div>
                              </v-col>

                              <v-col class="shrink">
                                <v-switch
                                  v-model="viewData.OrderScheduled.Delivery.Active"
                                  :disabled="!userCanManage"
                                  :label="viewData.OrderScheduled.Delivery.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                  class="ma-0 d-inline-flex"
                                  color="success"
                                  dark
                                  hide-details
                                  inset
                                />
                              </v-col>
                            </v-row>
                          </v-sheet>
                        </v-col>

                        <template v-if="viewData.OrderScheduled.Delivery.Active">
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.OrderScheduled.Delivery.Sources"
                              :items="orderScheduledSourceTypes"
                              :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              item-text="Name"
                              item-value="Id"
                              multiple
                              outlined
                              style="width: 100%; max-width: 350px;"
                            >
                              <template #selection="{item}">
                                <v-chip
                                  :color="item.Status ? 'green' : ''"
                                  :dark="item.Status"
                                  class="pl-0 ma-1"
                                  label
                                  small
                                >
                                  <v-avatar
                                    :color="item.Avatar.Color"
                                    class="mr-2"
                                    tile
                                  >
                                    <v-icon
                                      v-if="item.Avatar.Icon"
                                      class="white--text"
                                    >
                                      {{ item.Avatar.Icon }}
                                    </v-icon>

                                    <img
                                      v-else
                                      :src="item.Avatar.Img"
                                    >
                                  </v-avatar>

                                  <span>{{ item.Name }}</span>
                                </v-chip>
                              </template>

                              <template #item="data">
                                <v-list-item-avatar
                                  :color="data.item.Avatar.Color"
                                  :size="28"
                                >
                                  <v-icon
                                    v-if="data.item.Avatar.Icon"
                                    class="white--text"
                                  >
                                    {{ data.item.Avatar.Icon }}
                                  </v-icon>

                                  <img
                                    v-else
                                    :src="data.item.Avatar.Img"
                                  >
                                </v-list-item-avatar>

                                <v-list-item-content>
                                  <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                  <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </template>
                            </v-select>
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Delivery.StartTime"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.StartTime.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="24"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Delivery.EndTime"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.EndTime.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="24"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Delivery.MinimumOrderMinutesAhead"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.MinimumOrderMinutesAhead.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="360"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Delivery.DaysAhead"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.DaysAhead.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="360"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>
                        </template>
                      </v-row>
                    </v-container>
                  </v-col>

                  <v-col cols="6">
                    <v-container
                      class="pa-0"
                      fluid
                    >
                      <v-row dense>
                        <v-col cols="12">
                          <v-sheet
                            class="blue darken-3 white--text pa-2 mb-2"
                            rounded
                          >
                            <v-row class="align-center">
                              <v-col>
                                <div class="d-inline-flex">
                                  <span>{{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Title') }}</span>
                                  <v-icon
                                    class="white--text ml-2"
                                    small
                                  >
                                    {{ $t('Settings.Order.Field.GeneralOption.OrderTypes.Button.Collection.Icon') }}
                                  </v-icon>
                                </div>
                              </v-col>

                              <v-col class="shrink">
                                <v-switch
                                  v-model="viewData.OrderScheduled.Collection.Active"
                                  :disabled="!userCanManage"
                                  :label="viewData.OrderScheduled.Collection.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                  class="ma-0 d-inline-flex"
                                  color="success"
                                  dark
                                  hide-details
                                  inset
                                />
                              </v-col>
                            </v-row>
                          </v-sheet>
                        </v-col>

                        <template v-if="viewData.OrderScheduled.Collection.Active">
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.OrderScheduled.Collection.Sources"
                              :items="orderScheduledSourceTypes"
                              :label="$t('Restaurant.CatalogAddDialog.Field.SourceTypes.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              item-text="Name"
                              item-value="Id"
                              multiple
                              outlined
                              style="width: 100%; max-width: 350px;"
                            >
                              <template #selection="{item}">
                                <v-chip
                                  :color="item.Status ? 'green' : ''"
                                  :dark="item.Status"
                                  class="pl-0 ma-1"
                                  label
                                  small
                                >
                                  <v-avatar
                                    :color="item.Avatar.Color"
                                    class="mr-2"
                                    tile
                                  >
                                    <v-icon
                                      v-if="item.Avatar.Icon"
                                      class="white--text"
                                    >
                                      {{ item.Avatar.Icon }}
                                    </v-icon>

                                    <img
                                      v-else
                                      :src="item.Avatar.Img"
                                    >
                                  </v-avatar>

                                  <span>{{ item.Name }}</span>
                                </v-chip>
                              </template>

                              <template #item="data">
                                <v-list-item-avatar
                                  :color="data.item.Avatar.Color"
                                  :size="28"
                                >
                                  <v-icon
                                    v-if="data.item.Avatar.Icon"
                                    class="white--text"
                                  >
                                    {{ data.item.Avatar.Icon }}
                                  </v-icon>

                                  <img
                                    v-else
                                    :src="data.item.Avatar.Img"
                                  >
                                </v-list-item-avatar>

                                <v-list-item-content>
                                  <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                                  <v-list-item-subtitle>{{ data.item.Description | truncate }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </template>
                            </v-select>
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Collection.StartTime"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.StartTime.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="24"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Collection.EndTime"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.EndTime.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="24"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Collection.MinimumOrderMinutesAhead"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.MinimumOrderMinutesAhead.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="360"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model.number="viewData.OrderScheduled.Collection.DaysAhead"
                              :label="$t('Settings.Order.Field.PosOption.OrderScheduled.DaysAhead.Label')"
                              class="d-inline-flex my-1"
                              dense
                              hide-details
                              max="360"
                              min="1"
                              outlined
                              step="1"
                              style="width: 100%; max-width: 350px;"
                              type="number"
                            />
                          </v-col>
                        </template>
                      </v-row>
                    </v-container>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>

          <v-card flat>
            <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
              <v-icon class="pr-3">
                {{ $t('Settings.Order.Field.PosOption.Icon') }}
              </v-icon>
              <h5 class="text-body-2 font-weight-bold">
                <lang-search-highlight
                  :lang-text="$t('Settings.Order.Field.PosOption.Title')"
                  :search-text="langSearchText"
                  highlight
                />
              </h5>
            </v-card-title>

            <v-divider />

            <v-card-text>
              <v-container
                class="pa-0"
                fluid
              >
                <v-row
                  align="center"
                  dense
                >
                  <v-col cols="12">
                    <v-expansion-panels
                      accordion
                      flat
                    >
                      <v-expansion-panel class="elevation-0">
                        <v-expansion-panel-header class="py-0 pl-0">
                          <div>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.PosOption.SourceTypes.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.PosOption.SourceTypes.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <div class="divTable ml-4 mt-4">
                            <template v-for="(sourceType, index) in sourceTypesList">
                              <div
                                v-if="sourceType.Editable && !sourceType.DeletedAt"
                                :key="`sourcetype-${sourceType.Id}-${index}`"
                                :class="{'red lighten-5': !!sourceType.DeletedAt && !isDarkMode, 'red darken-2': !!sourceType.DeletedAt && isDarkMode}"
                                class="divTableRow"
                              >
                                <div
                                  class="divTableCell pa-2 text-left"
                                  style="width: 48px;"
                                >
                                  <v-avatar
                                    v-if="sourceType.Avatar.Icon || sourceType.Avatar.Img"
                                    :color="sourceType.Avatar.Color"
                                    size="48"
                                  >
                                    <v-icon
                                      v-if="sourceType.Avatar.Icon"
                                      dark
                                    >
                                      {{ sourceType.Avatar.Icon }}
                                    </v-icon>

                                    <img
                                      v-else-if="sourceType.Avatar.Img"
                                      :src="sourceType.Avatar.Img"
                                      height="48"
                                      width="48"
                                    >
                                  </v-avatar>
                                </div>

                                <div class="divTableCell pa-2 text-left">
                                  <div>
                                    <div>
                                      <lang-search-highlight
                                        :lang-text="sourceType.Title"
                                        :search-text="langSearchText"
                                        highlight
                                      />
                                    </div>
                                    <div class="text-caption grey--text">
                                      <lang-search-highlight
                                        :lang-text="`${sourceType.Description} / ${sourceType.SubTitle}`"
                                        :search-text="langSearchText"
                                        highlight
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div class="divTableCell pa-2 text-right">
                                  <v-switch
                                    v-model="sourceType.Active"
                                    :label="sourceType.Active ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                                    class="ma-0 d-inline-block"
                                    color="success"
                                    hide-details
                                    inset
                                  />
                                </div>
                              </div>

                              <template v-if="sourceType.Editable && sourceType.Active && !sourceType.DeletedAt">
                                <div
                                  :key="`sourcetype-order-machines-${sourceType.Id}-${index}`"
                                  class="divTableRow"
                                >
                                  <div
                                    class="divTableCell border-bottom-none pa-2 text-left"
                                    style="width: 48px;"
                                  />
                                  <div class="divTableCell pa-2 text-left">
                                    <v-select
                                      v-model="sourceType.Guid"
                                      :disabled="sourceType.GuidStatus === 0"
                                      :hint="$t('Restaurant.TableAreaAddDialog.Field.MachinesOrders.Hint')"
                                      :items="appMachineGroups"
                                      :label="$t('Restaurant.TableAreaAddDialog.Field.MachinesOrders.Label')"
                                      :prepend-inner-icon="$t('Restaurant.TableAreaAddDialog.Field.MachinesOrders.Icon')"
                                      deletable-chips
                                      dense
                                      filled
                                      item-text="Name"
                                      item-value="Id"
                                      multiple
                                      persistent-hint
                                      small-chips
                                    >
                                      <template #selection="{item, parent, selected}">
                                        <v-chip
                                          :value="selected"
                                          label
                                          small
                                        >
                                          <v-icon
                                            left
                                            small
                                          >
                                            mdi-desktop-classic
                                          </v-icon>

                                          <span class="pr-2">
                                            {{ item.Name }}
                                          </span>

                                          <v-icon
                                            small
                                            @click="parent.selectItem(item)"
                                          >
                                            close
                                          </v-icon>
                                        </v-chip>
                                      </template>
                                    </v-select>
                                  </div>

                                  <div class="divTableCell pa-2 text-right">
                                    <v-btn-toggle
                                      v-model="sourceType.GuidStatus"
                                      dense
                                      mandatory
                                    >
                                      <v-btn :value="0">
                                        DISABLED
                                      </v-btn>

                                      <v-btn :value="1">
                                        {{ sourceTypeIdsSupportGuidOverride.includes(sourceType.Id) ? 'MERGE' : 'ACTIVE' }}
                                      </v-btn>

                                      <v-btn
                                        v-if="sourceTypeIdsSupportGuidOverride.includes(sourceType.Id)"
                                        :value="2"
                                      >
                                        OVERRIDE
                                      </v-btn>
                                    </v-btn-toggle>
                                  </div>
                                </div>

                                <div
                                  :key="`sourcetype-fiscal-machines-${sourceType.Id}-${index}`"
                                  class="divTableRow"
                                >
                                  <div
                                    class="divTableCell pa-2 text-left"
                                    style="width: 48px;"
                                  />
                                  <div class="divTableCell pa-2 text-left">
                                    <v-select
                                      v-model="sourceType.GuidFiscal"
                                      :disabled="sourceType.GuidFiscalStatus === 0"
                                      :hint="$t('Restaurant.TableAreaAddDialog.Field.MachinesFiscal.Hint')"
                                      :items="appMachineGroups"
                                      :label="$t('Restaurant.TableAreaAddDialog.Field.MachinesFiscal.Label')"
                                      :prepend-inner-icon="$t('Restaurant.TableAreaAddDialog.Field.MachinesFiscal.Icon')"
                                      deletable-chips
                                      dense
                                      filled
                                      item-text="Name"
                                      item-value="Id"
                                      multiple
                                      persistent-hint
                                      small-chips
                                    >
                                      <template #selection="{item, parent, selected}">
                                        <v-chip
                                          :value="selected"
                                          label
                                          small
                                        >
                                          <v-icon
                                            left
                                            small
                                          >
                                            mdi-desktop-classic
                                          </v-icon>

                                          <span class="pr-2">
                                            {{ item.Name }}
                                          </span>

                                          <v-icon
                                            small
                                            @click="parent.selectItem(item)"
                                          >
                                            close
                                          </v-icon>
                                        </v-chip>
                                      </template>
                                    </v-select>
                                  </div>

                                  <div class="divTableCell pa-2 text-right">
                                    <v-btn-toggle
                                      v-model="sourceType.GuidFiscalStatus"
                                      dense
                                      mandatory
                                    >
                                      <v-btn :value="0">
                                        DISABLED
                                      </v-btn>

                                      <v-btn :value="1">
                                        {{ sourceTypeIdsSupportGuidOverride.includes(sourceType.Id) ? 'MERGE' : 'ACTIVE' }}
                                      </v-btn>

                                      <v-btn
                                        v-if="sourceTypeIdsSupportGuidOverride.includes(sourceType.Id)"
                                        :value="2"
                                      >
                                        OVERRIDE
                                      </v-btn>
                                    </v-btn-toggle>
                                  </div>
                                </div>
                              </template>
                            </template>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col cols="12">
                    <v-expansion-panels
                      accordion
                      flat
                    >
                      <v-expansion-panel class="elevation-0">
                        <v-expansion-panel-header class="py-0 pl-0">
                          <div>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.PosOption.OrderComments.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.PosOption.OrderComments.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                          <div class="divTable mt-4">
                            <div class="divTableHeading">
                              <div class="divTableRow">
                                <div class="divTableHead">
                                  &nbsp;
                                </div>

                                <div class="divTableHead">
                                  {{ $t('Settings.Order.Field.PosOption.OrderComments.Table.Header.Comment') }}
                                </div>

                                <div class="divTableHead text-right">
                                  <v-btn
                                    class="pa-2"
                                    color="primary"
                                    text
                                    @click="addComment"
                                  >
                                    <v-icon left>
                                      {{ $t('Settings.Order.Field.PosOption.OrderComments.Button.Add.Icon') }}
                                    </v-icon>&nbsp;
                                    {{ $t('Settings.Order.Field.PosOption.OrderComments.Button.Add.Title') }}
                                  </v-btn>
                                </div>
                              </div>
                            </div>

                            <draggable
                              v-model="viewData.OrderComments"
                              class="divTableBody"
                              handle=".my-handle"
                            >
                              <template v-for="(comment, index) in orderComments">
                                <div
                                  :key="`order-comment-${index}`"
                                  class="divTableRow"
                                >
                                  <div class="divTableCell">
                                    <v-icon
                                      class="my-handle"
                                      style="cursor: move;"
                                    >
                                      drag_handle
                                    </v-icon>
                                  </div>

                                  <div class="divTableCell pl-0 text-left">
                                    <v-text-field
                                      v-model="comment.Title"
                                      :placeholder="$t('Settings.Order.Field.PosOption.OrderComments.Table.Header.Comment')"
                                      clearable
                                      filled
                                      flat
                                      full-width
                                      hide-details
                                      single-line
                                    />
                                  </div>

                                  <div class="divTableCell pa-2 text-right">
                                    <v-switch
                                      v-model="comment.Active"
                                      :label="comment.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                      class="ma-0 d-inline-block mr-6"
                                      color="success"
                                      hide-details
                                      inset
                                    />

                                    <v-btn
                                      class="red ma-0 mb-2 mr-1"
                                      dark
                                      icon
                                      small
                                      @click="removeComment(index)"
                                    >
                                      <v-icon small>
                                        close
                                      </v-icon>
                                    </v-btn>
                                  </div>
                                </div>
                              </template>
                            </draggable>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>

                  <v-col cols="12">
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col cols="12">
                    <v-btn-toggle
                      v-model="viewData.VoipAddOrderDialog"
                      :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3'"
                      :disabled="!userCanManage"
                      color="success"
                      dense
                      mandatory
                    >
                      <v-btn
                        :disabled="!userCanManage"
                        :value="VoipAddOrderTypeEnum.AGENT"
                        class="text-caption font-weight-bold"
                        text
                      >
                        {{ $t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Item.Agent').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :disabled="!userCanManage"
                        :value="VoipAddOrderTypeEnum.MACHINE"
                        class="text-caption font-weight-bold"
                        text
                      >
                        {{ $t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Item.Machine').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :disabled="!userCanManage"
                        :value="VoipAddOrderTypeEnum.MIX"
                        class="text-caption font-weight-bold"
                        text
                      >
                        {{ $t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Item.Mix').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :disabled="!userCanManage"
                        :value="VoipAddOrderTypeEnum.EVERYONE"
                        class="text-caption font-weight-bold"
                        text
                      >
                        {{ $t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Item.Everyone').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :disabled="!userCanManage"
                        :value="VoipAddOrderTypeEnum.NONE"
                        class="text-caption font-weight-bold"
                        text
                      >
                        {{ $t('Settings.Order.Field.PosOption.VoipAddOrderDialog.Item.None').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Enabled.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Enabled.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.MenuOptionsListExpandableEnabled"
                      :disabled="!userCanManage"
                      :label="viewData.MenuOptionsListExpandableEnabled ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <template v-if="viewData.MenuOptionsListExpandableEnabled">
                    <v-col cols="12">
                      <v-divider class="" />
                    </v-col>

                    <v-col cols="12">
                      <v-card flat>
                        <v-card-text class="pa-0 pl-4">
                          <v-container
                            class="pa-0"
                            fluid
                          >
                            <v-row
                              align="center"
                              dense
                            >
                              <v-col>
                                <h5 class="text-body-2 font-weight-bold">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Mobile.Title')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </h5>

                                <span class="text-caption">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Mobile.Subtitle')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </span>
                              </v-col>
                              <v-col class="shrink">
                                <v-switch
                                  v-model="viewData.MenuOptionsListExpandableMobile"
                                  :disabled="!userCanManage"
                                  :label="viewData.MenuOptionsListExpandableMobile ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                  class="pa-0"
                                  color="success"
                                  hide-details
                                  inset
                                />
                              </v-col>

                              <v-col
                                class="py-0"
                                cols="12"
                              />

                              <v-col>
                                <h5 class="text-body-2 font-weight-bold">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Mobile.Multiple.Title')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </h5>

                                <span class="text-caption">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Mobile.Multiple.Subtitle')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </span>
                              </v-col>
                              <v-col class="shrink">
                                <v-switch
                                  v-model="viewData.MenuOptionsListExpandableMultipleMobile"
                                  :disabled="!userCanManage"
                                  :label="viewData.MenuOptionsListExpandableMultipleMobile ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                  class="ma-0"
                                  color="success"
                                  hide-details
                                  inset
                                />
                              </v-col>

                              <v-col cols="12">
                                <v-divider class="" />
                              </v-col>

                              <v-col>
                                <h5 class="text-body-2 font-weight-bold">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Desktop.Title')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </h5>

                                <span class="text-caption">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Desktop.Subtitle')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </span>
                              </v-col>
                              <v-col class="shrink">
                                <v-switch
                                  v-model="viewData.MenuOptionsListExpandableDesktop"
                                  :disabled="!userCanManage"
                                  :label="viewData.MenuOptionsListExpandableDesktop ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                  class="ma-0"
                                  color="success"
                                  hide-details
                                  inset
                                />
                              </v-col>

                              <v-col
                                class="py-0"
                                cols="12"
                              />

                              <v-col>
                                <h5 class="text-body-2 font-weight-bold">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Desktop.Multiple.Title')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </h5>

                                <span class="text-caption">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuOptionsListExpandable.Desktop.Multiple.Subtitle')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </span>
                              </v-col>
                              <v-col class="shrink">
                                <v-switch
                                  v-model="viewData.MenuOptionsListExpandableMultipleDesktop"
                                  :disabled="!userCanManage"
                                  :label="viewData.MenuOptionsListExpandableMultipleDesktop ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                  class="ma-0"
                                  color="success"
                                  hide-details
                                  inset
                                />
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </template>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.ShowSelectedMenuOptionsPos.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.ShowSelectedMenuOptionsPos.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.ShowSelectedMenuOptionsPos"
                      :disabled="!userCanManage"
                      :label="viewData.ShowSelectedMenuOptionsPos ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuItemQuickAdd.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuItemQuickAdd.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.MenuItemQuickAdd"
                      :disabled="!userCanManage"
                      :label="viewData.MenuItemQuickAdd ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuCategoriesTabsPosDesktop.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuCategoriesTabsPosDesktop.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink text-no-wrap">
                    <div>
                      <div class="d-inline-block">
                        <v-switch
                          v-model="MenuCategoriesTabsPosDesktop.Horizontal"
                          :disabled="!userCanManage"
                          :label="$t('Common.Button.Toggle.Horizontal')"
                          class="ma-0 "
                          color="success"
                          hide-details
                          inset
                          @change="onMenuCategoriesTabsPosDesktopHorizontalChange"
                        />
                      </div>

                      <div class="d-inline-block">
                        <v-switch
                          v-model="MenuCategoriesTabsPosDesktop.Vertical"
                          :disabled="!userCanManage"
                          :label="$t('Common.Button.Toggle.Vertical')"
                          class="ma-0 ml-2"
                          color="success"
                          hide-details
                          inset
                          @change="onMenuCategoriesTabsPosDesktopVerticalChange"
                        />
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuCategoriesTabsPosMobile.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuCategoriesTabsPosMobile.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink text-no-wrap">
                    <div>
                      <div class="d-inline-block">
                        <v-switch
                          v-model="MenuCategoriesTabsPosMobile.Horizontal"
                          :disabled="!userCanManage"
                          :label="$t('Common.Button.Toggle.Horizontal')"
                          class="ma-0 "
                          color="success"
                          hide-details
                          inset
                          @change="onMenuCategoriesTabsPosMobileHorizontalChange"
                        />
                      </div>

                      <div class="d-inline-block">
                        <v-switch
                          v-model="MenuCategoriesTabsPosMobile.Vertical"
                          :disabled="!userCanManage"
                          :label="$t('Common.Button.Toggle.Vertical')"
                          class="ma-0 ml-2"
                          color="success"
                          hide-details
                          inset
                          @change="onMenuCategoriesTabsPosMobileVerticalChange"
                        />
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuCategoriesColorWidthPos.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuCategoriesColorWidthPos.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink text-no-wrap">
                    <div class="d-inline-block">
                      <v-select
                        v-model="viewData.MenuCategoriesColorWidthPos"
                        :items=" $t('Settings.Order.Field.PosOption.MenuCategoriesColorWidthPos.Items')"
                        :label="$t('Settings.Order.Field.PosOption.MenuCategoriesColorWidthPos.Title')"
                        color="green lighten-1"
                        hide-details
                        style="min-width: 300px;"
                      />
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuSearchEnabledPos.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuSearchEnabledPos.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.MenuSearchEnabledPos"
                      :disabled="!userCanManage"
                      :label="viewData.MenuSearchEnabledPos ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.ShowMenuImages.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.ShowMenuImages.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.ShowMenuImagesPos"
                      :disabled="!userCanManage"
                      :label="viewData.ShowMenuImagesPos ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <template v-if="viewData.ShowMenuImagesPos">
                    <v-col cols="12">
                      <v-divider class="" />
                    </v-col>

                    <v-col cols="12">
                      <v-card flat>
                        <v-card-text class="pa-0 pl-4">
                          <v-container
                            class="pa-0"
                            fluid
                          >
                            <v-row
                              align="center"
                              dense
                            >
                              <v-col>
                                <h5 class="text-body-2 font-weight-bold">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuListItemImageLeftPos.Title')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </h5>

                                <span class="text-caption">
                                  <lang-search-highlight
                                    :lang-text="$t('Settings.Order.Field.PosOption.MenuListItemImageLeftPos.Subtitle')"
                                    :search-text="langSearchText"
                                    highlight
                                  />
                                </span>
                              </v-col>
                              <v-col class="shrink text-no-wrap">
                                <div>
                                  <div class="d-inline-block">
                                    <v-switch
                                      v-model="MenuListItemImagePos.Left"
                                      :disabled="!userCanManage"
                                      :label="$t('Common.Button.Left')"
                                      class="ma-0"
                                      color="success"
                                      hide-details
                                      inset
                                      @change="onMenuListItemImagePosLeftChange"
                                    />
                                  </div>

                                  <div class="d-inline-block">
                                    <v-switch
                                      v-model="MenuListItemImagePos.Right"
                                      :disabled="!userCanManage"
                                      :label="$t('Common.Button.Right')"
                                      class="ma-0 ml-2"
                                      color="success"
                                      hide-details
                                      inset
                                      @change="onMenuListItemImagePosRightChange"
                                    />
                                  </div>
                                </div>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </template>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuItemCustomPrice.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.MenuItemCustomPrice.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.MenuItemCustomPrice"
                      :disabled="!userCanManage"
                      :label="viewData.MenuItemCustomPrice ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.BarcodeDelayBetweenScans.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.BarcodeDelayBetweenScans.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-text-field
                      v-model="viewData.BarcodeDelayBetweenScans"
                      :placeholder="$t('Settings.Order.Field.PosOption.BarcodeDelayBetweenScans.PlaceHolder')"
                      :suffix="$t('Settings.Order.Field.PosOption.BarcodeDelayBetweenScans.Suffix')"
                      dense
                      hide-details
                      min="1"
                      outlined
                      single-line
                      step="1"
                      style="min-width: 300px;"
                      type="number"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.BarcodePrefixProductUnitQuantity.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.BarcodePrefixProductUnitQuantity.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-combobox
                      v-model="viewData.BarcodePrefixProductUnitQuantity"
                      :label="$t('Settings.Order.Field.PosOption.BarcodePrefixProductUnitQuantity.PlaceHolder')"
                      :search-input.sync="AddNewBarcodePrefixQuantitySearch"
                      color="green lighten-1"
                      deletable-chips
                      hide-details
                      hide-selected
                      item-text="title"
                      item-value="id"
                      min="0"
                      multiple
                      small-chips
                      step="1"
                      style="min-width: 300px;"
                      type="number"
                    >
                      <template #no-data>
                        <v-list-item>
                          <span class="text-subtitle-1 mr-2">{{ $t('Common.Button.Add') }}: </span>

                          <v-chip
                            v-show="AddNewBarcodePrefixQuantitySearch"
                            label
                            small
                          >
                            {{ AddNewBarcodePrefixQuantitySearch }}
                          </v-chip>
                        </v-list-item>
                      </template>

                      <template #selection="{item, parent, selected}">
                        <v-chip
                          :value="selected"
                          label
                          small
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>

                          <v-icon
                            small
                            @click="parent.selectItem(item)"
                          >
                            close
                          </v-icon>
                        </v-chip>
                      </template>

                      <template #item="{item}">
                        <v-list-item-content>
                          <v-chip
                            dark
                            label
                            small
                          >
                            {{ item }}
                          </v-chip>
                        </v-list-item-content>
                      </template>
                    </v-combobox>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.BarcodePrefixProductUnitPrice.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.BarcodePrefixProductUnitPrice.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-combobox
                      v-model="viewData.BarcodePrefixProductUnitPrice"
                      :label="$t('Settings.Order.Field.PosOption.BarcodePrefixProductUnitPrice.PlaceHolder')"
                      :search-input.sync="AddNewBarcodePrefixPriceSearch"
                      color="green lighten-1"
                      deletable-chips
                      hide-details
                      hide-selected
                      item-text="title"
                      item-value="id"
                      multiple
                      small-chips
                      style="min-width: 300px;"
                      type="number"
                    >
                      <template #no-data>
                        <v-list-item>
                          <span class="text-subtitle-1 mr-2">{{ $t('Common.Button.Add') }}: </span>

                          <v-chip
                            v-show="AddNewBarcodePrefixPriceSearch"
                            label
                            small
                          >
                            {{ AddNewBarcodePrefixPriceSearch }}
                          </v-chip>
                        </v-list-item>
                      </template>

                      <template #selection="{item, parent, selected}">
                        <v-chip
                          :value="selected"
                          label
                          small
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>

                          <v-icon
                            small
                            @click="parent.selectItem(item)"
                          >
                            close
                          </v-icon>
                        </v-chip>
                      </template>

                      <template #item="{item}">
                        <v-list-item-content>
                          <v-chip
                            dark
                            label
                            small
                          >
                            {{ item }}
                          </v-chip>
                        </v-list-item-content>
                      </template>
                    </v-combobox>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.SubtractStockThreshold.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>

                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.SubtractStockThreshold.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <div class="mb-1">
                      <v-text-field
                        :value="`> ${viewData.SubtractStockThreshold.Tier1.Value}`"
                        dense
                        disabled
                        hide-details
                        outlined
                        single-line
                        style="min-width: 300px;"
                      >
                        <template #prepend-inner>
                          <v-chip
                            :color="viewData.SubtractStockThreshold.Default.Color"
                            class="pa-1 mr-2"
                            dark
                            label
                            style="height: 25px;"
                          >
                            <v-icon>all_inclusive</v-icon>
                          </v-chip>
                        </template>
                      </v-text-field>
                    </div>

                    <div class="mb-1">
                      <v-text-field
                        v-model="viewData.SubtractStockThreshold.Tier1.Value"
                        dense
                        hide-details
                        min="1"
                        outlined
                        single-line
                        step="1"
                        style="min-width: 300px;"
                        type="number"
                      >
                        <template #prepend-inner>
                          <v-chip
                            :color="viewData.SubtractStockThreshold.Tier1.Color"
                            class="pa-1 mr-2"
                            dark
                            label
                            style="height: 25px;"
                          >
                            <v-icon>all_inclusive</v-icon>
                          </v-chip>
                        </template>
                      </v-text-field>
                    </div>

                    <div class="mb-1">
                      <v-text-field
                        v-model="viewData.SubtractStockThreshold.Tier2.Value"
                        dense
                        hide-details
                        min="1"
                        outlined
                        single-line
                        step="1"
                        style="min-width: 300px;"
                        type="number"
                      >
                        <template #prepend-inner>
                          <v-chip
                            :color="viewData.SubtractStockThreshold.Tier2.Color"
                            class="pa-1 mr-2"
                            dark
                            label
                            style="height: 25px;"
                          >
                            <v-icon>all_inclusive</v-icon>
                          </v-chip>
                        </template>
                      </v-text-field>
                    </div>

                    <div class="mb-1">
                      <v-text-field
                        v-model="viewData.SubtractStockThreshold.Tier3.Value"
                        dense
                        hide-details
                        min="1"
                        outlined
                        single-line
                        step="1"
                        style="min-width: 300px;"
                        type="number"
                      >
                        <template #prepend-inner>
                          <v-chip
                            :color="viewData.SubtractStockThreshold.Tier3.Color"
                            class="pa-1 mr-2"
                            dark
                            label
                            style="height: 25px;"
                          >
                            <v-icon>all_inclusive</v-icon>
                          </v-chip>
                        </template>
                      </v-text-field>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <!-- Stay In Pos After Each Order -->
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.StayInPos.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.StayInPos.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>

                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.StayInPos.Enabled"
                      :disabled="!userCanManage"
                      :label="viewData.StayInPos.Enabled ? $t('Common.Button.Toggle.Inactive') : $t('Common.Button.Toggle.Active')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col cols="12" />

                  <template v-if="viewData.StayInPos.Enabled">
                    <v-col>
                      <v-select
                        v-model="viewData.StayInPos.Machines"
                        :hint="$t('Settings.Order.Field.PosOption.StayInPos.Machines.Hint')"
                        :item-text="(item) => item.alias ? `${item.alias} (${item.hostname})` : item.hostname"
                        :items="appMachines"
                        :label="$t('Settings.Order.Field.PosOption.StayInPos.Machines.Label')"
                        :prepend-inner-icon="$t('Settings.Order.Field.PosOption.StayInPos.Machines.Icon')"
                        clearable
                        deletable-chips
                        dense
                        filled
                        item-value="guid"
                        multiple
                        persistent-hint
                        small-chips
                      >
                        <template #item="{item}">
                          <v-list-item-avatar
                            :size="28"
                            color="blue"
                            dark
                          >
                            <v-icon
                              color="white"
                              small
                            >
                              {{ $t('Settings.Order.Field.PosOption.StayInPos.Machines.Icon') }}
                            </v-icon>
                          </v-list-item-avatar>

                          <v-list-item-content>
                            <v-list-item-title>{{ item.hostname }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.alias }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </template>

                        <template #selection="{item, parent, selected}">
                          <v-chip
                            :value="selected"
                            class="pl-0"
                            dark
                            label
                            small
                          >
                            <v-avatar
                              class="mr-2"
                              color="blue"
                              tile
                            >
                              <v-icon
                                color="white"
                                small
                              >
                                mdi-desktop-classic
                              </v-icon>
                            </v-avatar>

                            <span class="pr-2">
                              {{ item.alias ? `${item.alias} (${item.hostname})` : item.hostname }}
                            </span>

                            <v-icon
                              small
                              @click="parent.selectItem(item)"
                            >
                              close
                            </v-icon>
                          </v-chip>
                        </template>
                      </v-select>

                      <v-select
                        v-model="viewData.StayInPos.Users"
                        :hint="$t('Settings.Order.Field.PosOption.StayInPos.Users.Hint')"
                        :items="staffList"
                        :label="$t('Settings.Order.Field.PosOption.StayInPos.Users.Label')"
                        :prepend-inner-icon="$t('Settings.Order.Field.PosOption.StayInPos.Users.Icon')"
                        clearable
                        deletable-chips
                        dense
                        filled
                        item-text="Name"
                        item-value="Id"
                        multiple
                        persistent-hint
                        small-chips
                      >
                        <template #item="{item}">
                          <v-list-item-avatar
                            :color="`${item.Avatar.Color}`"
                            :size="28"
                            dark
                          >
                            <v-icon
                              color="white"
                              small
                            >
                              {{ item.Avatar.Icon }}
                            </v-icon>
                          </v-list-item-avatar>

                          <v-list-item-content>
                            <v-list-item-title>{{ item.Name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.Email }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </template>

                        <template #selection="{item, parent, selected}">
                          <v-chip
                            :value="selected"
                            class="pl-0"
                            dark
                            label
                            small
                          >
                            <v-avatar
                              :color="item.Avatar.Color"
                              class="mr-2"
                              tile
                            >
                              <v-icon
                                color="white"
                                small
                              >
                                {{ item.Avatar.Icon }}
                              </v-icon>
                            </v-avatar>

                            <span class="pr-2">
                              {{ item.Name }}
                            </span>

                            <v-icon
                              small
                              @click="parent.selectItem(item)"
                            >
                              close
                            </v-icon>
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col class="shrink ml-2">
                      <div class="font-weight-medium text-body-1 mb-1 text-center">
                        {{ $t('Settings.Order.Field.PosOption.StayInPos.Override.Title') }}
                      </div>

                      <v-btn-toggle
                        v-model="viewData.StayInPos.Override"
                        :disabled="!userCanManage"
                        color="success"
                        mandatory
                      >
                        <v-btn
                          :disabled="!userCanManage"
                          text
                          value="machine"
                        >
                          {{ $t('Settings.Order.Field.PosOption.StayInPos.Override.Machine').toLocaleUpperCase($i18n.locale) }}
                        </v-btn>

                        <v-btn
                          :disabled="!userCanManage"
                          text
                          value="user"
                        >
                          {{ $t('Settings.Order.Field.PosOption.StayInPos.Override.User').toLocaleUpperCase($i18n.locale) }}
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </template>

                  <v-col cols="12">
                    <v-divider class="my-2" />
                  </v-col>

                  <!-- Order Discounts -->
                  <v-col>
                    <h5 class="text-body-2 font-weight-bold">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.Discount.Title')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </h5>
                    <span class="text-caption">
                      <lang-search-highlight
                        :lang-text="$t('Settings.Order.Field.PosOption.Discount.Subtitle')"
                        :search-text="langSearchText"
                        highlight
                      />
                    </span>
                  </v-col>
                  <v-col class="shrink">
                    <v-switch
                      v-model="viewData.OrderDiscounts.Enabled"
                      :disabled="!userCanManage"
                      :label="viewData.OrderDiscounts.Enabled ? $t('Common.Button.Toggle.Inactive') : $t('Common.Button.Toggle.Active')"
                      class="ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </v-col>

                  <v-col
                    v-if="viewData.OrderDiscounts.Enabled"
                    cols="12"
                  >
                    <v-card
                      :color="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                      flat
                    >
                      <v-card-text class="pa-2">
                        <v-container
                          fluid
                          pa-0
                        >
                          <v-row class="mt-2">
                            <v-col
                              cols="12"
                              md="6"
                            >
                              <v-simple-table
                                class="pa-2 rounded"
                                style="border: thin solid rgba(255, 255, 255, 0.20);"
                              >
                                <thead>
                                  <tr>
                                    <th
                                      :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-3'"
                                      class="text-center text-h6 pa-1"
                                      colspan="3"
                                    >
                                      Ποσοστιαία Έκπτωση
                                    </th>
                                  </tr>
                                  <tr>
                                    <th class="text-center pa-1">
                                      Ποσοστό
                                    </th>

                                    <th
                                      class="text-center pa-1"
                                      style="width: 70px;"
                                    >
                                      <v-btn
                                        :disabled="orderCouponsPercent.length >= 10"
                                        class="primary white--text"
                                        depressed
                                        icon
                                        x-small
                                        @click="addOrderDiscountPercent()"
                                      >
                                        <v-icon>mdi-plus</v-icon>
                                      </v-btn>
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <template v-for="(coupon, couponIdx) in orderCouponsPercent">
                                    <tr :key="`order-coupon-percent-${couponIdx}`">
                                      <td class="pa-1 text-center">
                                        <v-text-field
                                          v-model.number="coupon.Discount"
                                          dense
                                          hide-details
                                          outlined
                                          single-line
                                          suffix="%"
                                        />
                                      </td>

                                      <td class="pa-1 text-center">
                                        <v-btn
                                          :disabled="!userCanManage"
                                          class="red white--text"
                                          depressed
                                          icon
                                          x-small
                                          @click="removeOrderDiscountPercent(couponIdx)"
                                        >
                                          <v-icon>mdi-close</v-icon>
                                        </v-btn>
                                      </td>
                                    </tr>
                                  </template>
                                </tbody>
                              </v-simple-table>
                            </v-col>

                            <v-col
                              cols="12"
                              md="6"
                            >
                              <v-simple-table
                                class="pa-2 rounded"
                                style="border: thin solid rgba(255, 255, 255, 0.20);"
                              >
                                <thead>
                                  <tr>
                                    <th
                                      :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-3'"
                                      class="text-center text-h6 pa-1"
                                      colspan="3"
                                    >
                                      Έκπτωση Ποσού
                                    </th>
                                  </tr>
                                  <tr>
                                    <th class="text-center pa-1">
                                      Ποσό
                                    </th>

                                    <th
                                      class="text-center pa-1"
                                      style="width: 70px;"
                                    >
                                      <v-btn
                                        :disabled="orderCouponsFixed.length >= 10"
                                        class="primary white--text"
                                        depressed
                                        icon
                                        x-small
                                        @click="addOrderDiscountFixed()"
                                      >
                                        <v-icon>mdi-plus</v-icon>
                                      </v-btn>
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <template v-for="(coupon, couponIdx) in orderCouponsFixed">
                                    <tr :key="`order-coupon-fixed-${couponIdx}`">
                                      <td class="pa-1 text-center">
                                        <v-text-field
                                          v-model.number="coupon.Discount"
                                          dense
                                          hide-details
                                          outlined
                                          single-line
                                          suffix="€"
                                        />
                                      </td>

                                      <td class="pa-1 text-center">
                                        <v-btn
                                          :disabled="!userCanManage"
                                          class="red white--text"
                                          depressed
                                          icon
                                          x-small
                                          @click="removeOrderDiscountFixed(couponIdx)"
                                        >
                                          <v-icon>mdi-close</v-icon>
                                        </v-btn>
                                      </td>
                                    </tr>
                                  </template>
                                </tbody>
                              </v-simple-table>
                            </v-col>
                          </v-row>

                          <v-row class="ma-1">
                            <v-col>
                              <h5 class="text-body-2 font-weight-bold">
                                <lang-search-highlight
                                  :lang-text="$t('Settings.Order.Field.PosOption.Discount.Custom.Title')"
                                  :search-text="langSearchText"
                                  highlight
                                />
                              </h5>
                              <span class="text-caption">
                                <lang-search-highlight
                                  :lang-text="$t('Settings.Order.Field.PosOption.Discount.Custom.Subtitle')"
                                  :search-text="langSearchText"
                                  highlight
                                />
                              </span>
                            </v-col>
                            <v-col class="shrink">
                              <v-switch
                                v-model="viewData.OrderDiscounts.CustomEnabled"
                                :disabled="!userCanManage"
                                :label="viewData.OrderDiscounts.CustomEnabled ? $t('Common.Button.Toggle.Inactive') : $t('Common.Button.Toggle.Active')"
                                class="ma-0"
                                color="success"
                                hide-details
                                inset
                              />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>

          <!-- External Platforms -->
          <v-card
            class="mb-4"
            flat
          >
            <v-card-title :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-4']">
              <v-icon class="pr-3">
                {{ $t('Settings.Order.Field.ExternalPlatforms.Icon') }}
              </v-icon>
              <h5 class="text-body-2 font-weight-bold">
                <lang-search-highlight
                  :lang-text="$t('Settings.Order.Field.ExternalPlatforms.Title')"
                  :search-text="langSearchText"
                  highlight
                />
              </h5>
            </v-card-title>

            <v-divider />

            <v-expansion-panels
              accordion
              flat
            >
              <!-- Delivery Hero -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <h4>
                    <lang-search-highlight
                      :lang-text="$t('Settings.Order.Field.ExternalPlatforms.Platform.DeliveryHero')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h4>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-card flat>
                    <v-card-text>
                      <v-container
                        class="pa-0"
                        fluid
                      >
                        <v-row
                          align="center"
                          dense
                        >
                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAutoCreateCustomer"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAutoCreateCustomer ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAutoCreateAddress"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAutoCreateAddress ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAutoPrintOrder"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAutoPrintOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAutoPrintPreparationOrder"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAutoPrintPreparationOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAutoPrintReceipt"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAutoPrintReceipt ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.CatalogImportUseItemPrintedName.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.CatalogImportUseItemPrintedName.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroCatalogImportUseItemPrintedName"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroCatalogImportUseItemPrintedName ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAddTip"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAddTip ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroAddDelivery"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroAddDelivery ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.DeliveryHeroRemoveCustomerPhonePrefix"
                              :disabled="!userCanManage"
                              :label="viewData.DeliveryHeroRemoveCustomerPhonePrefix ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col cols="12">
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.DeliveryHeroDefaultOrderStatus"
                              :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                              :label="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                              clearable
                              filled
                              hide-details
                              item-text="Name"
                              item-value="Id"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>

                <v-divider class="" />
              </v-expansion-panel>

              <!-- Box -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <h4>
                    <lang-search-highlight
                      :lang-text="$t('Settings.Order.Field.ExternalPlatforms.Platform.Box')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h4>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-card flat>
                    <v-card-text>
                      <v-container
                        class="pa-0"
                        fluid
                      >
                        <v-row
                          align="center"
                          dense
                        >
                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAutoCreateCustomer"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAutoCreateCustomer ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAutoCreateAddress"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAutoCreateAddress ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAutoPrintOrder"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAutoPrintOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAutoPrintPreparationOrder"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAutoPrintPreparationOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAutoPrintReceipt"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAutoPrintReceipt ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAddTip"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAddTip ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxAddDelivery"
                              :disabled="!userCanManage"
                              :label="viewData.BoxAddDelivery ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.BoxRemoveCustomerPhonePrefix"
                              :disabled="!userCanManage"
                              :label="viewData.BoxRemoveCustomerPhonePrefix ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col cols="12">
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.BoxDefaultOrderStatus"
                              :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                              :label="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                              clearable
                              filled
                              hide-details
                              item-text="Name"
                              item-value="Id"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>

                <v-divider class="" />
              </v-expansion-panel>

              <!-- Wolt -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <h4>
                    <lang-search-highlight
                      :lang-text="$t('Settings.Order.Field.ExternalPlatforms.Platform.Wolt')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h4>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-card flat>
                    <v-card-text>
                      <v-container
                        class="pa-0"
                        fluid
                      >
                        <v-row
                          align="center"
                          dense
                        >
                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAutoCreateCustomer"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAutoCreateCustomer ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAutoCreateAddress"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAutoCreateAddress ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAutoPrintOrder"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAutoPrintOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAutoPrintPreparationOrder"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAutoPrintPreparationOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAutoPrintReceipt"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAutoPrintReceipt ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAddTip"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAddTip ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltAddDelivery"
                              :disabled="!userCanManage"
                              :label="viewData.WoltAddDelivery ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.WoltRemoveCustomerPhonePrefix"
                              :disabled="!userCanManage"
                              :label="viewData.WoltRemoveCustomerPhonePrefix ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col cols="12">
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.WoltDefaultOrderStatus"
                              :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                              :label="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                              clearable
                              filled
                              hide-details
                              item-text="Name"
                              item-value="Id"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>

                <v-divider class="" />
              </v-expansion-panel>

              <!-- Fagi -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <h4>
                    <lang-search-highlight
                      :lang-text="$t('Settings.Order.Field.ExternalPlatforms.Platform.Fagi')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h4>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-card flat>
                    <v-card-text>
                      <v-container
                        class="pa-0"
                        fluid
                      >
                        <v-row
                          align="center"
                          dense
                        >
                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAutoCreateCustomer"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAutoCreateCustomer ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAutoCreateAddress"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAutoCreateAddress ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAutoPrintOrder"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAutoPrintOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAutoPrintPreparationOrder"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAutoPrintPreparationOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAutoPrintReceipt"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAutoPrintReceipt ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAddTip"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAddTip ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiAddDelivery"
                              :disabled="!userCanManage"
                              :label="viewData.FagiAddDelivery ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.FagiRemoveCustomerPhonePrefix"
                              :disabled="!userCanManage"
                              :label="viewData.FagiRemoveCustomerPhonePrefix ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col cols="12">
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.FagiDefaultOrderStatus"
                              :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                              :label="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                              clearable
                              filled
                              hide-details
                              item-text="Name"
                              item-value="Id"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>

                <v-divider class="" />
              </v-expansion-panel>

              <!-- GetFood -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <h4>
                    <lang-search-highlight
                      :lang-text="$t('Settings.Order.Field.ExternalPlatforms.Platform.GetFood')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h4>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-card flat>
                    <v-card-text>
                      <v-container
                        class="pa-0"
                        fluid
                      >
                        <v-row
                          align="center"
                          dense
                        >
                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateCustomer.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAutoCreateCustomer"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAutoCreateCustomer ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoCreateAddress.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAutoCreateAddress"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAutoCreateAddress ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAutoPrintOrder"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAutoPrintOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintPreparationOrder.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAutoPrintPreparationOrder"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAutoPrintPreparationOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AutoPrintReceipt.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAutoPrintReceipt"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAutoPrintReceipt ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddTip.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAddTip"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAddTip ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.AddDelivery.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodAddDelivery"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodAddDelivery ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.RemoveCustomerPhonePrefix.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodRemoveCustomerPhonePrefix"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodRemoveCustomerPhonePrefix ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.OrderUpdateStatusInSource.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.OrderUpdateStatusInSource.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodOrderUpdateStatusInSource"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodOrderUpdateStatusInSource ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col>
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.OrderDeleteInSource.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.OrderDeleteInSource.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col class="shrink">
                            <v-switch
                              v-model="viewData.GetFoodOrderDeleteInSource"
                              :disabled="!userCanManage"
                              :label="viewData.GetFoodOrderDeleteInSource ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                              class="ma-0"
                              color="success"
                              hide-details
                              inset
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="" />
                          </v-col>

                          <v-col cols="12">
                            <h5 class="text-body-2 font-weight-bold">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Title')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </h5>

                            <span class="text-caption">
                              <lang-search-highlight
                                :lang-text="$t('Settings.Order.Field.ExternalPlatforms.InitOrderStatus.Subtitle')"
                                :search-text="langSearchText"
                                highlight
                              />
                            </span>
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="viewData.GetFoodDefaultOrderStatus"
                              :items="$te('Settings.Payment.Edit.Field.OrderStatus.Items') && Array.isArray($t('Settings.Payment.Edit.Field.OrderStatus.Items')) ? $t('Settings.Payment.Edit.Field.OrderStatus.Items') : []"
                              :label="$t('Settings.Payment.Edit.Field.OrderStatus.Label')"
                              clearable
                              filled
                              hide-details
                              item-text="Name"
                              item-value="Id"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>

                <v-divider class="" />
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card>
        </v-form>
      </template>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />
      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        to="/system-settings"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
      <v-btn
        v-if="userCanManage"
        :disabled="viewData===null || isSaving"
        :loading="isSaving"
        class="green white--text elevation-0"
        @click="onSaveOrdersClick"
      >
        {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import Translatable from '@/mixins/translatable'
import SourceType from '@/mixins/orders/sourceType'
import draggable from 'vuedraggable'
import LangSearchHighlightMixin from '@/lang/highlight/langSearchHighlightMixin.js'
import LangSearchHighlight from '@/lang/highlight/LangSearchHighlight.vue'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum.js'
import {clone, mergeDeep, sortBy} from '@/lib/helper/helper.js'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum.js'
import OrderSourceTypeChip from '@/components/orders/common/OrderSourceTypeChip.vue'
import OrderPaymentsChip from '@/components/orders/common/OrderPaymentsChip.vue'
import PaymentTypeEnum from '../../api/Enums/PaymentTypeEnum.js'
import StaffGroupEnum from '@/api/Enums/StaffGroupEnum.js'
import VoipAddOrderTypeEnum from '@/api/Enums/VoipAddOrderTypeEnum.js'

export default {
  components: {OrderPaymentsChip, OrderSourceTypeChip, draggable: draggable, LangSearchHighlight},
  mixins    : [AppData, Auth, Translatable, SourceType, LangSearchHighlightMixin],
  data() {
    return {
      orderCouponsPercent         : [],
      orderCouponsFixed           : [],
      settingsFormValid           : false,
      validationRules             : {},
      menuOrdersDailyOpenVisible  : false,
      menuOrdersDailyCloseVisible : false,
      MenuCategoriesTabsPosDesktop: {
        Horizontal: false,
        Vertical  : false
      },
      MenuCategoriesTabsPosMobile: {
        Horizontal: false,
        Vertical  : false
      },
      MenuListItemImagePos: {
        Left : false,
        Right: false
      },
      isSaving                         : false,
      viewData                         : null,
      AddNewBarcodePrefixQuantitySearch: '',
      AddNewBarcodePrefixPriceSearch   : '',
      sourceTypesList                  : []
    }
  },
  computed: {
    VoipAddOrderTypeEnum() {
      return VoipAddOrderTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    SourceTypeEnum() {
      return SourceTypeEnum
    },

    staffList() {
      const users = clone(window?.appConfig?.POS_USERS || [])
      return users
        .filter(o => StaffGroupEnum.POS_ORDER.includes(o.Group))
        .filter(o => !this.userIsSuperAdmin ? !StaffGroupEnum.SYSTEM.includes(o.Group) : true)
        .map(o => {
          return {
            Id    : o.Id,
            Name  : o.Fullname,
            Email : o.Uid,
            Group : o.Group,
            Avatar: {
              Icon : o.Active ? 'mdi-account-check-outline' : !o.Uid ? 'mdi-account-group' : 'mdi-account-alert-outline',
              Color: o.Active ? 'success' : !o.Uid ? 'info' : 'grey darken-1'
            }
          }
        })
    },

    orderStatusesFiltered() {
      const defaultStatuses = this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items').filter(o => ![OrderStatusEnum.Completed, OrderStatusEnum.Canceled].includes(o.StatusId))
      const allStatus = [this.$t('Order.OrdersAll.Filter.filterOrderStatus.ItemAll')]

      return clone([...allStatus, ...defaultStatuses]).map(o => {
        if (o.StatusId === OrderStatusEnum.Received) o.disabled = true
        return o
      })
    },

    orderStatusesFromTo() {
      const defaultStatuses = this.$t('Order.OrdersAll.Filter.filterOrderStatus.Items').filter(o => OrderStatusEnum.FromToStatuses.includes(o.Id))
      const unknownStatus = [this.$t('Order.OrdersAll.Filter.filterOrderStatus.ItemUnknown')]
      return clone([...unknownStatus, ...defaultStatuses])
    },

    orderStatusFromToSourceTypes() {
      return clone(this.activeSourceTypesAll).map(o => {
        o.disabled = !!this.viewData?.OrderStatuses?.find(i => o.Id === i.Source)
        return o
      })
    },

    orderScheduledSourceTypes() {
      const activeSources = clone(this.activeSourceTypesAll)
      return activeSources.filter(s => [SourceTypeEnum.ALL, SourceTypeEnum.POS, ...SourceTypeEnum.ONLINE_SOURCES].includes(s.Id))
    },

    activePaymentMethods() {
      return this.orderBy(this.viewData?.Payments || [], 'Site')
    },

    orderComments() {
      return this.viewData?.OrderComments || []
    }
  },
  watch: {
    'viewData.OrderScheduled.Sources': function (newVal, oldVal) {
      const all = this.orderScheduledSourceTypes.find(s => s.Id === SourceTypeEnum.ALL)

      if (newVal.length > 1) {
        if (newVal.includes(SourceTypeEnum.ALL) && !oldVal.includes(SourceTypeEnum.ALL)) {
          this.viewData.OrderScheduled.Sources = [SourceTypeEnum.ALL]
          all.disabled = true
        } else if (newVal.includes(SourceTypeEnum.ALL)) {
          this.viewData.OrderScheduled.Sources = newVal.filter(sourceType => sourceType !== SourceTypeEnum.ALL)
          all.disabled = false
        }
      }
    },
    'viewData.DeliveryHeroAutoCreateCustomer': function (newVal) {
      if (!newVal) this.viewData.DeliveryHeroAutoCreateAddress = false
    },
    'viewData.DeliveryHeroAutoCreateAddress': function (newVal) {
      if (newVal) this.viewData.DeliveryHeroAutoCreateCustomer = true
    },

    'viewData.GetFoodAutoCreateCustomer': function (newVal) {
      if (!newVal) this.viewData.GetFoodAutoCreateAddress = false
    },
    'viewData.GetFoodAutoCreateAddress': function (newVal) {
      if (newVal) this.viewData.GetFoodAutoCreateCustomer = true
    }
  },
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Settings.Orders.Get, this.onGetOrdersResult)
    this.$bus.$on(window.SocketCommand.Settings.Orders.Save, this.onSaveOrdersResult)
    this.sourceTypesList = [...this.sourceTypesDefault(), ...this.sourceTypesOther()]
    this.getData()
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Settings.Orders.Get, this.onGetOrdersResult)
    this.$bus.$off(window.SocketCommand.Settings.Orders.Save, this.onSaveOrdersResult)
  },
  methods: {
    initDefaultOrderStatusOverrideBySourceType() {
      this.activeSourceTypes
        .filter(sourceType => !SourceTypeEnum.IS_MARKETPLACE_SOURCE(sourceType.Id) && ![SourceTypeEnum.TABLE].includes(sourceType.Id))
        .forEach(sourceType => {
          const obj = this.viewData.DefaultOrderStatusOverrideBySourceType.SourceTypes.find(o => o.SourceTypeId === sourceType.Id)
          if (!obj) {
            this.viewData.DefaultOrderStatusOverrideBySourceType.SourceTypes.push({
              SourceTypeId         : sourceType.Id,
              DefaultOrderStatusPos: this.viewData.DefaultOrderStatusPos ?? OrderStatusEnum.Accepted
            })
          }
        })

      this.viewData.DefaultOrderStatusOverrideBySourceType.SourceTypes = this.viewData.DefaultOrderStatusOverrideBySourceType.SourceTypes.filter(override => {
        return this.activeSourceTypes.find(active => active.Id === override.SourceTypeId)
      })
    },

    addOrderStatusSource() {
      const payload = {
        Source: null,
        Status: {
          Normal: [
            {
              From: 0,
              To  : 21
            },
            {
              From: 11,
              To  : 21
            },
            {
              From: 12,
              To  : 21
            },
            {
              From: 21,
              To  : 13
            },
            {
              From: 13,
              To  : 14
            },
            {
              From: 14,
              To  : 15
            }
          ],
          Scheduled: [
            {
              From: 0,
              To  : 21
            },
            {
              From: 11,
              To  : 21
            },
            {
              From: 12,
              To  : 21
            },
            {
              From: 21,
              To  : 13
            },
            {
              From: 13,
              To  : 14
            },
            {
              From: 14,
              To  : 15
            }
          ]
        }
      }

      this.viewData.OrderStatuses.push(payload)
    },

    removeOrderStatusSource(index) {
      this.viewData.OrderStatuses.splice(index, 1)
    },

    addOrderDiscountPercent() {
      if (this.orderCouponsPercent.length >= 10) return

      this.orderCouponsPercent.push({
        Type    : 'P',
        Name    : 'Coupon',
        Code    : '',
        Discount: '',
        RowId   : '',
        Custom  : ''
      })
    },
    removeOrderDiscountPercent(index) {
      this.orderCouponsPercent.splice(index, 1)
    },

    addOrderDiscountFixed() {
      if (this.orderCouponsFixed.length >= 10) return

      this.orderCouponsFixed.push({
        Type    : 'F',
        Name    : 'Coupon',
        Code    : '',
        Discount: '',
        RowId   : '',
        Custom  : ''
      })
    },
    removeOrderDiscountFixed(index) {
      this.orderCouponsFixed.splice(index, 1)
    },

    addOrderStatusSourceNormal(orderStatusIdx) {
      this.viewData.OrderStatuses[orderStatusIdx].Status.Normal.push({From: null, To: null})
    },
    removeOrderStatusSourceNormal(orderStatusIdx, index) {
      this.viewData.OrderStatuses[orderStatusIdx].Status.Normal.splice(index, 1)
    },
    resetOrderStatusSourceNormal(orderStatusIdx, orderStatusSource) {
      const item = this.viewData?.OrderStatusesDefaults?.find(o => o.Source === orderStatusSource)
      const payload = clone(item?.Status?.Normal || [])
      this.viewData.OrderStatuses[orderStatusIdx].Status.Normal = payload
    },

    addOrderStatusSourceScheduled(orderStatusIdx) {
      this.viewData.OrderStatuses[orderStatusIdx].Status.Scheduled.push({From: null, To: null})
    },
    removeOrderStatusSourceScheduled(orderStatusIdx, index) {
      this.viewData.OrderStatuses[orderStatusIdx].Status.Scheduled.splice(index, 1)
    },
    resetOrderStatusSourceScheduled(orderStatusIdx, orderStatusSource) {
      const item = this.viewData?.OrderStatusesDefaults?.find(o => o.Source === orderStatusSource)
      const payload = clone(item?.Status?.Scheduled || [])
      this.viewData.OrderStatuses[orderStatusIdx].Status.Scheduled = payload
    },

    addOrderTimeUpdateSource() {
      const payload = {
        active: true,
        source: 0,
        status: 0
      }

      this.viewData.OrderTimeUpdate.push(payload)
    },

    removeOrderTimeUpdateSource(index) {
      this.viewData.OrderTimeUpdate.splice(index, 1)
    },

    onMenuCategoriesTabsPosDesktopVerticalChange(value) {
      this.MenuCategoriesTabsPosDesktop.Horizontal = !value
      this.viewData.MenuCategoriesTabsPos = this.MenuCategoriesTabsPosDesktop.Horizontal
    },
    onMenuCategoriesTabsPosDesktopHorizontalChange(value) {
      this.MenuCategoriesTabsPosDesktop.Vertical = !value
      this.viewData.MenuCategoriesTabsPos = this.MenuCategoriesTabsPosDesktop.Horizontal
    },

    onMenuCategoriesTabsPosMobileVerticalChange(value) {
      this.MenuCategoriesTabsPosMobile.Horizontal = !value
      this.viewData.MenuCategoriesTabsPosMobile = this.MenuCategoriesTabsPosMobile.Horizontal
    },
    onMenuCategoriesTabsPosMobileHorizontalChange(value) {
      this.MenuCategoriesTabsPosMobile.Vertical = !value
      this.viewData.MenuCategoriesTabsPosMobile = this.MenuCategoriesTabsPosMobile.Horizontal
    },

    onMenuListItemImagePosLeftChange(value) {
      this.MenuListItemImagePos.Right = !value
      this.viewData.MenuListItemImageLeftPos = this.MenuListItemImagePos.Left
    },
    onMenuListItemImagePosRightChange(value) {
      this.MenuListItemImagePos.Left = !value
      this.viewData.MenuListItemImageLeftPos = this.MenuListItemImagePos.Left
    },

    sourceTypesDefault() {
      const excludeIds = [0]
      let sourceTypesDefault = Object.values(this.appConfig?.SOURCE_TYPES?.DEFAULT || {})

      sourceTypesDefault = sourceTypesDefault.map(o => {
        return {
          Id              : parseInt(o.source_type_id),
          Title           : this.getOrderSourceTypeLangById(o.source_type_id)?.Title || o.title,
          SubTitle        : o.name,
          Description     : this.getOrderSourceTypeLangById(o.source_type_id)?.Desc || o.desc,
          Active          : !!o.active,
          Deleted         : this.userIsSuperAdmin ? false : !!o.deleted_at,
          DeletedAt       : o.deleted_at,
          Editable        : this.userIsSuperAdmin,
          Guid            : o.guid || [],
          GuidStatus      : o.guid_status || 0,
          GuidFiscal      : o.guid_fiscal || [],
          GuidFiscalStatus: o.guid_fiscal_status || 0,
          Avatar          : {
            Icon : this.getOrderSourceTypeLangById(o.source_type_id)?.Icon || o.logo,
            Color: 'blue-grey'
          }
        }
      }).filter(o => !excludeIds.includes(o.Id))
        .filter(o => !o.Deleted)

      return sourceTypesDefault
    },

    sourceTypesOther() {
      const excludeIds = []
      let sourceTypesOther = this.appConfig?.SOURCE_TYPES?.OTHER || []

      sourceTypesOther = sourceTypesOther.map(o => {
        return {
          Id              : parseInt(o.source_type_id),
          Title           : o.title,
          SubTitle        : o.name,
          Description     : o.desc,
          Active          : !!o.active,
          Deleted         : this.userIsSuperAdmin ? false : !!o.deleted_at,
          DeletedAt       : o.deleted_at,
          Editable        : this.userIsSuperAdmin,
          Guid            : o.guid || [],
          GuidStatus      : o.guid_status || 0,
          GuidFiscal      : o.guid_fiscal || [],
          GuidFiscalStatus: o.guid_fiscal_status || 0,
          Avatar          : {
            Img  : o.logo,
            Color: 'blue-grey'
          }
        }
      }).filter(o => !excludeIds.includes(o.Id))
        .filter(o => !o.Deleted)

      return sourceTypesOther
    },

    addComment() {
      if (!this.viewData?.OrderComments) {
        this.viewData.OrderComments = []
      }

      this.viewData.OrderComments.unshift({
        Title : '',
        Active: false
      })
    },

    removeComment(index) {
      if (!this.viewData?.OrderComments) return
      this.viewData.OrderComments.splice(index, 1)
    },

    getData() {
      window.callAS(window.SocketCommand.Settings.Orders.Get, {})
    },

    onGetOrdersResult(result) {
      if (result) {
        if (result.OrdersDailyOpen && result.OrdersDailyClose) {
          result.OrdersDailyOpen = `${result.OrdersDailyOpen.split(':')[0]}:${result.OrdersDailyOpen.split(':')[1]}`
          result.OrdersDailyClose = `${result.OrdersDailyClose.split(':')[0]}:${result.OrdersDailyClose.split(':')[1]}`
        }

        result.SubtractStockThreshold = {
          ...(!this.viewData?.SubtractStockThreshold ? {} : result.SubtractStockThreshold),
          ...this.$t('Settings.Order.Field.SubtractStockThreshold.Values')
        }
      }

      this.viewData = result

      this.MenuCategoriesTabsPosDesktop.Horizontal = result.MenuCategoriesTabsPos
      this.MenuCategoriesTabsPosDesktop.Vertical = !result.MenuCategoriesTabsPos

      this.MenuCategoriesTabsPosMobile.Horizontal = result.MenuCategoriesTabsPosMobile
      this.MenuCategoriesTabsPosMobile.Vertical = !result.MenuCategoriesTabsPosMobile

      this.MenuListItemImagePos.Left = result.MenuListItemImageLeftPos
      this.MenuListItemImagePos.Right = !result.MenuListItemImageLeftPos

      if (!this.viewData?.DefaultOrderStatusOverrideBySourceType) {
        this.$set(this.viewData, 'DefaultOrderStatusOverrideBySourceType', {
          Enabled    : false,
          SourceTypes: []
        })
      }
      this.initDefaultOrderStatusOverrideBySourceType()

      if (!this.viewData.OrderDiscounts) {
        this.$set(this.viewData, 'OrderDiscounts', {
          Enabled      : true,
          CustomEnabled: true,
          Coupons      : [
            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 5,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 10,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 0.5,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 1,
              RowId   : '',
              Custom  : ''
            },

            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 15,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 20,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 1.5,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 2,
              RowId   : '',
              Custom  : ''
            },

            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 25,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 30,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 2.5,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 3,
              RowId   : '',
              Custom  : ''
            },

            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 40,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 50,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 5,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 10,
              RowId   : '',
              Custom  : ''
            },

            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 75,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'P',
              Name    : 'Coupon',
              Code    : '',
              Discount: 100,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 15,
              RowId   : '',
              Custom  : ''
            },
            {
              Type    : 'F',
              Name    : 'Coupon',
              Code    : '',
              Discount: 20,
              RowId   : '',
              Custom  : ''
            }
          ]
        })
      }


      const stayInPosDefaultObj = {
        Enabled : false,
        Override: 'machine',
        Machines: [],
        Users   : []
      }
      const stayInPos = mergeDeep(stayInPosDefaultObj, this.viewData?.StayInPos || {})
      this.$set(this.viewData, 'StayInPos', stayInPos)


      this.orderCouponsFixed = this.viewData?.OrderDiscounts?.Coupons?.filter(c => c.Type === 'F') || []
      this.orderCouponsPercent = this.viewData?.OrderDiscounts?.Coupons?.filter(c => c.Type === 'P') || []
    },

    onSaveOrdersClick() {
      this.$refs?.settingsForm && this.$refs.settingsForm.validate()

      if (this.settingsFormValid) {
        this.isSaving = true
        this.initDefaultOrderStatusOverrideBySourceType()

        const payload = JSON.parse(JSON.stringify(this.viewData))

        payload.SourceTypes = JSON.parse(JSON.stringify(this.sourceTypesList, ['Id', 'Active', 'Title', 'Guid', 'GuidStatus', 'GuidFiscal', 'GuidFiscalStatus']))

        payload.OrderComments = payload.OrderComments?.filter(comment => !!comment?.Title?.trim()) || []

        payload.OrderDiscounts.Coupons = [...sortBy(this.orderCouponsFixed, 'Discount', 'asc'), ...sortBy(this.orderCouponsPercent, 'Discount', 'asc')]

        window.callAS(window.SocketCommand.Settings.Orders.Save, {Orders: payload})
      }
    },

    onSaveOrdersResult(result) {
      this.isSaving = false
      if (result.status) {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: result.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Order.Notification'),
          type: 'error',
          icon: 'warning'
        })
      }
    }
  }
}
</script>

<style scoped>

:deep(.v-expansion-panel__header) {
  padding: 0;
}

.divTableCell, .divTableHead {
  padding: 3px 4px;
}

.divTableHead {
  padding: 4px 10px;
}
</style>
